/**
 * Parse Drillz table metadata into geometry visual config.
 */

const CATEGORICAL_PALETTE = [
  '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
  '#911eb4', '#42d4f4', '#f032e6', '#bfef45', '#fabed4',
  '#469990', '#dcbeff', '#9A6324', '#fffac8', '#800000',
];

/**
 * @param {object|null|undefined} raw
 * @returns {import('../features/geometry/geometrySceneTypes').GeometryVisualConfig}
 */
export function parseGeometryVisualConfig(raw) {
  const m = raw || {};
  return {
    geometryVisualMode: m.geometryVisualMode || 'none',
    geometryVisualFieldCode: m.geometryVisualFieldCode ?? null,
    geometryRadiusFieldCode: m.geometryRadiusFieldCode ?? null,
    continuousIntervals: Boolean(m.continuousIntervals),
    allowOverlaps: Boolean(m.allowOverlaps),
  };
}

function hashColor(text) {
  let h = 0;
  const s = String(text);
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) | 0;
  return CATEGORICAL_PALETTE[Math.abs(h) % CATEGORICAL_PALETTE.length];
}

/**
 * @param {object} layerResponse From drillz_geometry_visuals API
 * @returns {import('../features/geometry/geometrySceneTypes').LayerVisualBundle}
 */
export function buildLayerVisualBundle(layerResponse) {
  const config = parseGeometryVisualConfig(layerResponse.config);
  const rowsById = new Map();
  const rawRows = layerResponse.rowsById || {};
  Object.entries(rawRows).forEach(([id, row]) => {
    rowsById.set(String(id), row);
  });

  const legendEntries = [];
  const listValues = layerResponse.listValues || [];
  if (listValues.length) {
    listValues.forEach((lv) => {
      legendEntries.push({ value: lv.value, color: lv.color || hashColor(lv.value) });
    });
  } else if (config.geometryVisualMode === 'list' && config.geometryVisualFieldCode) {
    const seen = new Set();
    for (const row of rowsById.values()) {
      const val = row[config.geometryVisualFieldCode];
      if (val == null || seen.has(val)) continue;
      seen.add(val);
      legendEntries.push({ value: String(val), color: hashColor(String(val)) });
    }
  }

  let numericLegend = null;
  if (config.geometryVisualMode === 'numeric' && config.geometryVisualFieldCode) {
    let min = Infinity;
    let max = -Infinity;
    for (const row of rowsById.values()) {
      const v = parseFloat(row[config.geometryVisualFieldCode]);
      if (!Number.isFinite(v)) continue;
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
    if (Number.isFinite(min) && Number.isFinite(max)) {
      numericLegend = { min, max };
    }
  }

  return {
    tableDefinitionId: layerResponse.tableDefinitionId,
    tableCode: layerResponse.tableCode ?? null,
    tableDisplayName: layerResponse.tableDisplayName || layerResponse.tableCode || 'Layer',
    config,
    byRowId: rowsById,
    legendEntries,
    numericLegend,
  };
}

/**
 * @param {string} value
 * @param {import('../features/geometry/geometrySceneTypes').LayerVisualBundle} bundle
 */
export function colorForRow(sourceRowId, bundle) {
  const row = bundle.byRowId.get(String(sourceRowId));
  const field = bundle.config.geometryVisualFieldCode;
  if (!row || !field) return '#888888';

  const raw = row[field];
  if (raw == null) return '#888888';

  if (bundle.config.geometryVisualMode === 'numeric') {
    const v = parseFloat(raw);
    if (!Number.isFinite(v) || !bundle.numericLegend) return '#888888';
    const { min, max } = bundle.numericLegend;
    const t = max > min ? (v - min) / (max - min) : 0.5;
    return numericRampColor(t);
  }

  const entry = bundle.legendEntries.find((e) => e.value === String(raw));
  return entry?.color || hashColor(String(raw));
}

/**
 * @param {string} sourceRowId
 * @param {import('../features/geometry/geometrySceneTypes').LayerVisualBundle} bundle
 * @param {number} [defaultMul=1]
 */
export function radiusMultiplierForRow(sourceRowId, bundle, defaultMul = 1) {
  const field = bundle.config.geometryRadiusFieldCode;
  if (!field || bundle.config.geometryVisualMode !== 'numeric') return defaultMul;
  const row = bundle.byRowId.get(String(sourceRowId));
  if (!row) return defaultMul;
  const v = parseFloat(row[field]);
  if (!Number.isFinite(v) || !bundle.numericLegend) return defaultMul;
  const { min, max } = bundle.numericLegend;
  if (max <= min) return defaultMul;
  return 0.5 + ((v - min) / (max - min)) * 1.5;
}

function numericRampColor(t) {
  const clamped = Math.max(0, Math.min(1, t));
  const r = Math.round(255 * clamped);
  const b = Math.round(255 * (1 - clamped));
  return `rgb(${r}, 80, ${b})`;
}

export { CATEGORICAL_PALETTE, hashColor };
